import {
  ComponentPropsWithoutRef,
  forwardRef,
  ReactNode,
  useMemo,
} from 'react';

import classNames from 'classnames';

import './Link.scss';

import { TestMetadata } from '../types';

export interface LinkProps extends ComponentPropsWithoutRef<'a'> {
  /** Elements to Wrap */
  children?: ReactNode;
  /** Custom class name(s) for component container */
  className?: string;
  /** Disables the Link */
  disabled?: boolean;
  /** Address of the Link */
  href?: string;
  /**
   * Relationship between the location in the document containing the hyperlink and the destination resource.
   * Omitted if the href attribute is not present.
   * Default is "noopener noreferrer" for security reasons: https://web.dev/external-anchors-use-rel-noopener/
   */
  rel?: string;
  /** Text underline decoration*/
  underline?: 'none' | 'hover' | 'always';
  // Test IDs for component sections
  testMetadata?: TestMetadata;
}

const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  (
    {
      children,
      className,
      disabled,
      href,
      onClick,
      testMetadata,
      rel = 'noopener noreferrer',
      underline = 'none',
      ...otherProps
    },
    ref,
  ) => {
    const buttonGroupClassNames = useMemo(
      () =>
        classNames(
          'sb-link',
          `sb-link_underline_${underline}`,
          {
            'sb-link_disabled': disabled,
          },
          className,
        ),
      [className, underline, disabled],
    );

    return (
      <a
        ref={ref}
        aria-disabled={disabled}
        className={buttonGroupClassNames}
        href={!disabled ? href : undefined}
        onClick={!disabled ? onClick : undefined}
        rel={href && rel}
        {...testMetadata}
        {...otherProps}
      >
        {children}
      </a>
    );
  },
);

export default Link;
